// ============================================
// protraction.js - "protraction" leg per candle (protraction-20261018).
// Protraction = the first phase of a candle: the move away from the open
// AGAINST the way the candle closes, which makes the wick extreme.
//   bullish (C > O): P = low   (dip under the open)
//   bearish (C < O): P = high  (push over the open)
//   doji (C == O) or P == O: nothing drawn
// O = first trade price, C = last trade price, H/L with print times, all from
// the tape buffer (data.rawTradesRef). One line (open time, O) -> (time of P, P),
// x by time inside the candle, optional dot at P.
//   - closed candles: one scan when first seen, cached by startTime
//   - live candle: incremental (only trades since the last call); P flips
//     sides automatically when the live price crosses the open
//   - candles older than the tape buffer (~3h): no data -> no line
// ============================================

export const PROTRACTION_DEFAULTS = {
  color: '#ff9f43',
  opacity: 0.6,
  dots: true,
  lineWidth: 1.5,
  minCandlePx: 12,
}

export function normalizeProtractionCfg(c) {
  const o = { ...PROTRACTION_DEFAULTS, ...(c && typeof c === 'object' ? c : {}) }
  o.opacity = Math.max(0, Math.min(1, Number(o.opacity)))
  if (!Number.isFinite(o.opacity)) o.opacity = PROTRACTION_DEFAULTS.opacity
  o.dots = !!o.dots
  if (typeof o.color !== 'string' || !o.color) o.color = PROTRACTION_DEFAULTS.color
  o.lineWidth = Number(o.lineWidth)
  if (!(o.lineWidth > 0)) o.lineWidth = PROTRACTION_DEFAULTS.lineWidth
  o.minCandlePx = Number(o.minCandlePx)
  if (!Number.isFinite(o.minCandlePx) || o.minCandlePx < 0) o.minCandlePx = PROTRACTION_DEFAULTS.minCandlePx
  return o
}

// cache: { key, map: Map<startTime, rec> }
// rec: { i: next buffer index, op, cp, hi, lo, hT, lT, o: buffer oldestTime, done }
function firstIdx(buf, t) {
  let lo = 0, hi = buf.length
  while (lo < hi) { const m = (lo + hi) >> 1; if (buf.timeAt(m) < t) lo = m + 1; else hi = m }
  return lo
}

function resetRec(r, buf, cs) {
  r.i = firstIdx(buf, cs)
  r.op = 0; r.cp = 0
  r.hi = -Infinity; r.lo = Infinity; r.hT = 0; r.lT = 0
}

// -> rec | null. live = forming candle (keep scanning on later calls).
export function protractionFor(cache, buf, cs, ivMs, live) {
  if (!buf || !buf.length || !(ivMs > 0)) return null
  let r = cache.map.get(cs)
  if (r && r.done) return r.hi >= r.lo ? r : null
  if (!r) {
    if (!(buf.oldestTime() <= cs)) return null        // tape doesn't cover it
    r = { done: false }
    resetRec(r, buf, cs)
    if (cache.map.size > 4000) cache.map.clear()
    cache.map.set(cs, r)
  } else if (r.i > buf.length || r.o !== buf.oldestTime()) {
    // buffer was restructured (trim/prepend) - rescan this candle from scratch
    resetRec(r, buf, cs)
  }
  r.o = buf.oldestTime()   // trim/prepend shifts indices -> detected next call
  const ce = cs + ivMs
  const n = buf.length
  let i = r.i
  for (; i < n; i++) {
    const t = buf.timeAt(i)
    if (t >= ce) break
    const p = buf.mcapAt(i)
    if (!(p > 0)) continue
    if (!(r.op > 0)) r.op = p
    r.cp = p
    if (p > r.hi) { r.hi = p; r.hT = t }
    if (p < r.lo) { r.lo = p; r.lT = t }
  }
  r.i = i
  if (!live) r.done = true
  return r.hi >= r.lo ? r : null
}

// Queue one candle's protraction leg on the current path.
// x0/x1 = body left/right px, yOf = price->y, out = dot list (x,y,...) or null.
export function drawProtraction(ctx, r, cs, ivMs, x0, x1, yOf, out) {
  const O = r.op, C = r.cp
  if (!(O > 0) || !(C > 0) || C === O) return          // no trades / doji
  const bull = C > O
  const P = bull ? r.lo : r.hi
  const pT = bull ? r.lT : r.hT
  if (!(P > 0) || P === O) return                         // no deviation from the open
  const yO = yOf(O), yP = yOf(P)
  if (!Number.isFinite(yO) || !Number.isFinite(yP)) return
  const w = x1 - x0
  const xP = x0 + w * Math.max(0, Math.min(1, (pT - cs) / ivMs))
  ctx.moveTo(x0, yO); ctx.lineTo(xP, yP)
  if (out) out.push(xP, yP)
}
